/**
 * Treatment Selection functionality
 * Handles search, sorting, and view toggle for treatment cards
 */

class TreatmentSelection {
  constructor(container) {
    this.container = container;
    this.searchInput = container.querySelector('[data-search-input]');
    this.sortDropdown = container.querySelector('[data-sort-dropdown]');
    this.sortButton = container.querySelector('[data-sort-button]');
    this.sortMenu = container.querySelector('[data-sort-menu]');
    this.sortCurrent = container.querySelector('[data-sort-current]');
    this.sortOptions = container.querySelectorAll('[data-sort-option]');
    this.viewButtons = container.querySelectorAll('[data-view]');
    this.treatmentsGrid = container.querySelector('[data-treatments-grid]');
    this.treatmentCards = container.querySelectorAll('[data-treatment-card]');
    this.categoryFilters = container.querySelectorAll('[data-category]');

    this.currentSort = 'popular';
    this.currentView = 'grid';
    this.currentCategory = 'all';
    this.searchTerm = '';

    this.init();
  }

  init() {
    this.bindEvents();
    this.initializeCards();
  }

  bindEvents() {
    // Search functionality
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.#handleSearch.bind(this));
    }

    // Sort dropdown functionality
    if (this.sortButton) {
      this.sortButton.addEventListener('click', this.#toggleSortDropdown.bind(this));
    }

    if (this.sortOptions) {
      for (const option of this.sortOptions) {
        option.addEventListener('click', this.#handleSortChange.bind(this));
      }
    }

    // View toggle functionality
    if (this.viewButtons) {
      for (const button of this.viewButtons) {
        button.addEventListener('click', this.#handleViewChange.bind(this));
      }
    }

    // Category filter functionality
    if (this.categoryFilters) {
      for (const filter of this.categoryFilters) {
        filter.addEventListener('click', this.#handleCategoryChange.bind(this));
      }
    }

    // Close dropdown when clicking outside
    document.addEventListener('click', this.#handleOutsideClick.bind(this));

    // Close dropdown on escape key
    document.addEventListener('keydown', this.#handleKeydown.bind(this));
  }

  initializeCards() {
    // Store original order for reset
    this.originalOrder = Array.from(this.treatmentCards);
    this.#applyCurrentSort();
  }

  #handleSearch(event) {
    this.searchTerm = event.target.value.toLowerCase().trim();
    this.#filterAndSort();
  }

  #toggleSortDropdown(event) {
    event.stopPropagation();
    this.sortDropdown.classList.toggle('treatment-selection__sort-dropdown--open');
  }

  #handleSortChange(event) {
    const sortType = event.target.dataset.sortOption;
    if (!sortType) return;

    this.currentSort = sortType;
    this.sortCurrent.textContent = event.target.textContent;
    this.sortDropdown.classList.remove('treatment-selection__sort-dropdown--open');
    this.#applyCurrentSort();
  }

  #handleViewChange(event) {
    const view = event.target.closest('[data-view]')?.dataset.view;
    if (!view || view === this.currentView) return;

    this.currentView = view;

    // Update active state
    for (const button of this.viewButtons) {
      button.classList.remove('treatment-selection__view-button--active');
    }
    event.target.closest('[data-view]').classList.add('treatment-selection__view-button--active');

    // Update grid view
    if (view === 'list') {
      this.treatmentsGrid.classList.add('treatment-selection__grid--list');
    } else {
      this.treatmentsGrid.classList.remove('treatment-selection__grid--list');
    }
  }

  #handleCategoryChange(event) {
    const category = event.target.dataset.category;
    if (!category || category === this.currentCategory) return;

    this.currentCategory = category;

    // Update active state
    for (const filter of this.categoryFilters) {
      filter.classList.remove('treatment-selection__category-filter--active');
    }
    event.target.classList.add('treatment-selection__category-filter--active');

    this.#filterAndSort();
  }

  #handleOutsideClick(event) {
    if (!this.sortDropdown.contains(event.target)) {
      this.sortDropdown.classList.remove('treatment-selection__sort-dropdown--open');
    }
  }

  #handleKeydown(event) {
    if (event.key === 'Escape') {
      this.sortDropdown.classList.remove('treatment-selection__sort-dropdown--open');
    }
  }

  #filterAndSort() {
    // Filter cards based on search term
    for (const card of this.treatmentCards) {
      const isVisible = this.#shouldShowCard(card);
      card.classList.toggle('treatment-selection__card--hidden', !isVisible);
    }

    this.#applyCurrentSort();
  }

  #shouldShowCard(card) {
    // Check category filter
    if (this.currentCategory !== 'all') {
      const cardCategory = card.dataset.category || '';
      if (cardCategory !== this.currentCategory) return false;
    }

    // Check search term
    if (!this.searchTerm) return true;

    const name = card.dataset.name || '';
    const treatmentName = card.querySelector('.treatment-selection__treatment-name')?.textContent.toLowerCase() || '';
    const category = card.querySelector('.treatment-selection__category')?.textContent.toLowerCase() || '';

    return (
      name.includes(this.searchTerm) || treatmentName.includes(this.searchTerm) || category.includes(this.searchTerm)
    );
  }

  #applyCurrentSort() {
    const visibleCards = Array.from(this.treatmentCards).filter(
      (card) => !card.classList.contains('treatment-selection__card--hidden')
    );

    const sortedCards = this.#sortCards(visibleCards);

    // Reorder cards in DOM
    for (const card of sortedCards) {
      this.treatmentsGrid.appendChild(card);
    }
  }

  #sortCards(cards) {
    return cards.sort((a, b) => {
      switch (this.currentSort) {
        case 'price-low':
          return this.#compareByPrice(a, b, false);
        case 'price-high':
          return this.#compareByPrice(a, b, true);
        case 'name':
          return this.#compareByName(a, b);
        case 'popular':
        default:
          return this.#compareByPopularity(a, b);
      }
    });
  }

  #compareByPrice(a, b, descending = false) {
    const priceA = parseFloat(a.dataset.price) || 0;
    const priceB = parseFloat(b.dataset.price) || 0;
    return descending ? priceB - priceA : priceA - priceB;
  }

  #compareByName(a, b) {
    const nameA = a.querySelector('.treatment-selection__treatment-name')?.textContent.toLowerCase() || '';
    const nameB = b.querySelector('.treatment-selection__treatment-name')?.textContent.toLowerCase() || '';
    return nameA.localeCompare(nameB);
  }

  #compareByPopularity(a, b) {
    // For popularity, we'll use the original order from the DOM
    const indexA = this.originalOrder.indexOf(a);
    const indexB = this.originalOrder.indexOf(b);
    return indexA - indexB;
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const treatmentSections = document.querySelectorAll('.treatment-selection');

  for (const section of treatmentSections) {
    new TreatmentSelection(section);
  }
});

// Re-initialize on theme editor updates
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.treatment-selection')) {
    new TreatmentSelection(event.target.querySelector('.treatment-selection'));
  }
});
